import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Opens the cart drawer when an add to cart leaves sets and their pieces short
 * of shared stock (see snippets/cart-shared-stock.liquid). Shopify accepts such
 * an add, so without this the shopper would only find out at checkout.
 *
 * The cart drawer section rendered after the add carries `.cart__shared-stock-note`
 * when the cart is in conflict.
 */

const DRAWER_SECTION_ID = 'cart-drawer-section';
const CONFLICT_SELECTOR = '.cart__shared-stock-note';

/**
 * @param {string | undefined} html
 * @returns {boolean | null} Null when there is no HTML to check.
 */
function hasConflict(html) {
  if (!html) return null;
  return new DOMParser().parseFromString(html, 'text/html').querySelector(CONFLICT_SELECTOR) !== null;
}

function openDrawer() {
  /** @type {import('@theme/theme-drawer').ThemeDrawer | null | undefined} */
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && !drawer.isOpen) drawer.open();
}

/** @param {import('@shopify/events').CartLinesUpdateEvent} event */
function onCartLinesUpdate(event) {
  if (event.action !== 'add') return;

  // An add from quick add runs inside a modal dialog; open the drawer once it
  // closes, as cart-drawer.js does for auto-open, so focus returns correctly.
  const sourceModal = /** @type {HTMLDialogElement | null} */ (
    event.target instanceof Element ? event.target.closest('dialog:modal') : null
  );

  event.promise
    ?.then(async ({ detail }) => {
      // An error does not mean nothing was added: asked for more than is in
      // stock, Shopify adds what it can and still answers with an error
      // ("only N were added"), without rendered sections. Check a fresh render
      // then, as for add sources that do not pass sections at all.
      const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
      const conflict =
        hasConflict(detail?.didError ? undefined : sections?.[DRAWER_SECTION_ID]) ??
        hasConflict(await sectionRenderer.getSectionHTML(DRAWER_SECTION_ID, false));
      if (!conflict) return;

      if (sourceModal?.open) {
        sourceModal.addEventListener('close', openDrawer, { once: true });
      } else {
        openDrawer();
      }
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-shared-stock] Event promise rejected:', error);
    });
}

document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
